import React from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

// config 
import config from '../../data/layout-config';

// custom hooks
import useRouteMatch from '../../hooks/useRouteMatch';

// context
import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { useSiteSettings } from '../../context/SiteSettingsProvider/SiteSettingsProvider';

// components
import { NioBrand, NioIcon } from '..';

export default function Footer({ variant = 1, children }) {

  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const location = useLocation();
  const jasspLogoPaths = [
    '/',
    '/realisations',
    '/contact-us',
    '/expertises',
    '/partenaires',
    '/digital-twin',
    '/blockchain',
    '/e-living-lab',
  ];
  const jasspLogoSrc =
    jasspLogoPaths.includes(location.pathname) || location.pathname.startsWith('/expertises/')
      ? (settings.siteLogoUrl || '/images/logo-jassp.png')
      : undefined;

  const compClasses = classNames({
    [`${useRouteMatch("/") ? "nk-footer-landing" : "nk-footer"}`]: true,

    // bg color
    "bg-dark": useRouteMatch(["/index-crypto-profile"]),
    "bg-blue-1700": useRouteMatch(["/index-bs-analytics"]),
    "bg-gray-1200": useRouteMatch(["/index-bs-consulting", "/index-fintech"]),
    "bg-gray-1100": useRouteMatch(["/index-crypto-profile", "/index-coaching-service"]),
    "is-theme": useRouteMatch(["/index-bs-analytics", "/index-coaching-service", "/index-crypto-profile", "/index-bs-consulting", "/index-fintech"])
  });


  const socialIconClasses = classNames({

    // border radius
    "rounded-1": !useRouteMatch(["/index-crypto-profile"]),

    // bg colors
    "text-bg-purple": useRouteMatch(["/index-kids-course"]),
    "text-bg-white": useRouteMatch(["/index-crypto-profile"]),
    "text-bg-success-alt text-white": useRouteMatch(["/index-live-chat-app"]),
    "text-bg-info text-white": useRouteMatch(["/index-bs-management"]),
    "text-bg-info-alt text-white": useRouteMatch(["/index-language-learning"]),
    "text-bg-indigo text-white": useRouteMatch(["/index-collaboration-tool", "/index-bs-solution", "/about-solution", "/features-solution", "/contact-us-solution", "/pricing-solution"]),
    "text-bg-primary-alt text-white": useRouteMatch(["/index-bs-expense-tracker", "/index-saas", "/index-bs-subscription", "/index-data-driven", "/contact-us"]),
    "text-bg-success text-white": useRouteMatch(["/index-bs-digital"]),
    "text-bg-primary": !useRouteMatch(["/index-kids-course", "/index-crypto-profile", "/index-live-chat-app", "/index-bs-management", "/index-language-learning", "/index-collaboration-tool", "/index-bs-solution", "/index-bs-expense-tracker", "/index-saas", "/index-bs-subscription", "/index-data-driven", "/index-bs-digital", "/contact-us"]),
  });

  const brandLinkClasses = classNames({
    "text-info": useRouteMatch(["/index-bs-management"]),
    "text-success-alt": useRouteMatch(["/index-live-chat-app"]),
    "text-success": useRouteMatch(["/index-bs-digital"]),
    "text-info-alt": useRouteMatch(["/index-language-learning"]),
    "text-purple": useRouteMatch(["/index-kids-course", "/index-crypto-profile"]),
    "text-primary-alt": useRouteMatch(["/index-bs-expense-tracker", "/index-saas", "/index-bs-subscription", "/index-data-driven", "/contact-us"]),
    "text-indigo": useRouteMatch(["/index-collaboration-tool", "/index-bs-solution", "/about-solution", "/features-solution", "/contact-us-solution", "/pricing-solution"]),
    "text-primary": !useRouteMatch(["/index-kids-course", "/index-crypto-profile", "/index-live-chat-app", "/index-bs-management", "/index-language-learning", "/index-collaboration-tool", "/index-bs-solution", "/index-bs-expense-tracker", "/index-saas", "/index-bs-subscription", "/index-data-driven", "/index-bs-digital", "/contact-us"])
  });


  const footerTextClasses = classNames({
    // typography
    "fs-16": useRouteMatch(["/index-kids-course"]),
    "fw-medium": useRouteMatch(["/index-kids-course"]),
    "text-capitalize": true,

    // colors
    "text-dark": !useRouteMatch(["/index-crypto-profile"]),
  });

  // variants of navbar 
  function filterDataByVariant(variantNumber) {
    return config.filter(item => item.variant === variantNumber);
  }
  const [data] = filterDataByVariant(variant)

  return (
    <footer className={compClasses}>
      {
        data.footer.variant === 1 ?
          <>
            <div className="call-to">
              <div className="nk-mask z-1 blur-6"></div>
              <Container>
                <div className="call-to-content z-1 position-relative is-theme py-7">
                  <Row className="justify-content-center">
                    <Col xl={6}>
                      <div className="call-to-info text-center">
                        <NioBrand className="mb-3" logo={data.footer.logo?.name || "s1"} variant={data.footer.logo?.variant || "dark"} customSrc={jasspLogoSrc} />
                        <h2 className="mb-1">{t('footer.v1.ctaTitle')}</h2>
                        <p className="m-0">{t('footer.v1.ctaLead')}</p>
                        <div className="call-to-action pt-5 pt-lg-7">
                          <Link to="/realisations" target="_blank" className="btn btn-primary mb-1">
                            <NioIcon name="bag-fill" className="me-2" />
                            {t('footer.v1.purchase')}
                          </Link>
                          <Link to="/realisations" target="_blank">
                            <img src="/images/icon/envato.png" alt="envato" />
                            <span>{t('footer.v1.envato')}</span>
                          </Link>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Container>
            </div>
            <div className="nk-footer-landing-copyright py-3">
              <Container>
                <p className="mb-0 text-capitalize">
                  Copyright &copy; <span className="text-white">{new Date().getFullYear()}</span> {settings.companyName || 'JASSP SAS'}.
                </p>
              </Container>
            </div>
          </>
          :
          data.footer.variant === 2 ?
            <section className={compClasses}>
              <div className="nk-footer-top">
                <Container>
                  <Row className="nk-footer-content justify-content-xl-between">
                    <Col md={8} lg={4} xl={4}>
                      <div className="nk-footer-brand pb-5 pb-lg-0">
                        <div className="nk-footer-brand-info mb-4">
                          <div className="nk-footer-logo">
                            <NioBrand logo={data.footer.logo?.name} variant={data.footer.logo?.variant} customSrc={jasspLogoSrc} />
                          </div>
                          <p>
                            <strong>{t('footer.v2.contactIntro')}</strong> <br />
                            <strong>{t('footer.v2.emailLabel')}</strong> {settings.footerEmail || 'direction@jassp.com'} <br />
                            <strong>{t('footer.v2.phoneLabel')}</strong> {settings.contactPhone || '+33 6 74 42 29 57'} <br />
                            <strong>{t('footer.v2.addressLabel')}</strong> {settings.contactAddressLine1 || '2B Rue Alfred Nobel'} {settings.contactAddressLine2 || '77420 Champs-sur-Marne, France'}
                          </p>
                        </div>
                        <ul className="nk-footer-social">
                          {settings.socialFacebook && (
                            <li>
                              <a href={settings.socialFacebook} target="_blank" rel="noopener noreferrer" className={socialIconClasses}>
                                <NioIcon name="facebook-f" />
                              </a>
                            </li>
                          )}
                          {settings.socialLinkedin && (
                            <li>
                              <a href={settings.socialLinkedin} target="_blank" rel="noopener noreferrer" className={socialIconClasses}>
                                <NioIcon name="linkedin" />
                              </a>
                            </li>
                          )}
                          {settings.socialTwitter && (
                            <li>
                              <a href={settings.socialTwitter} target="_blank" rel="noopener noreferrer" className={socialIconClasses}>
                                <NioIcon name="twitter" />
                              </a>
                            </li>
                          )}
                          {settings.socialInstagram && (
                            <li>
                              <a href={settings.socialInstagram} target="_blank" rel="noopener noreferrer" className={socialIconClasses}>
                                <NioIcon name="instagram" />
                              </a>
                            </li>
                          )}
                          {settings.socialWhatsapp && (
                            <li>
                              <a href={`https://wa.me/${settings.socialWhatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className={socialIconClasses}>
                                <NioIcon name="whatsapp" />
                              </a>
                            </li>
                          )}
                        </ul>
                      </div>
                    </Col>
                    <Col lg={8} xl={6}>
                      <Row className="justify-content-between">
                        <Col sm={8} md={7}>
                          <div className="nk-footer-info">
                            <h5 className="title">{t('footer.v2.pages')}</h5>
                            <ul className="row gy-1 gy-sm-4">
                              <li className="col-6">
                                <Link to="/">{t('footer.v2.linkHome')}</Link>
                              </li>

                              <li className="col-6">
                                <Link to="/realisations">{t('footer.v2.linkRealisations')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/expertises">{t('footer.v2.linkExpertises')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/partenaires">{t('footer.v2.linkPartenaires')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/qui-sommes-nous">{t('footer.v2.linkQuiSommesNous')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/notre-histoire">{t('footer.v2.linkNotreHistoire')}</Link>
                              </li>
                              <li className="col-6">
                                <Link to="/pourquoi-nous-choisir">{t('footer.v2.linkPourquoiNousChoisir')}</Link>
                              </li>

                              <li className="col-6">
                                <Link to="/contact-us">{t('footer.v2.linkContact')}</Link>
                              </li>
                            </ul>
                          </div>
                        </Col>
                        <Col sm={4}>
                          <div className="nk-footer-info">
                            <h5 className="title">{t('footer.v2.utility')}</h5>
                            <ul className="row gy-1 gy-sm-4">
                              <li className="col-12">
                                <Link to="/auth/login">{t('footer.v2.login')}</Link>
                              </li>
                              <li className="col-12">
                                <Link to="/auth/sign-up">{t('footer.v2.signup')}</Link>
                              </li>
                              <li className="col-12">
                                <Link to="/auth/reset-password">{t('footer.v2.resetPassword')}</Link>
                              </li>
                             
                            </ul>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </div>
              <div className="nk-footer-bottom">
                <Container>
                  <Row className="nk-footer-content justify-content-between">
                    <Col lg={6} className="px-0">
                      <p className="nk-footer-copyright-text text-center text-lg-start">&copy; 2008 - <span id="currentYear"> {new Date().getFullYear()} </span>
                        <Link className={brandLinkClasses} to="https://jassp.com/" target="_blank"> {settings.companyName || 'JASSP SAS'}</Link>. {t('footer.v2.rightsReserved')}
                      </p>
                    </Col>
                    <Col lg={6} className="px-0">
                      <ul className="nk-footer-copyright justify-content-center justify-content-lg-end">
                        <li>
                          <Link className={footerTextClasses} to="#">{t('footer.v2.allRights')}</Link>
                        </li>
                        <li>
                          <Link className={footerTextClasses} to="#">{t('footer.v2.terms')}</Link>
                        </li>
                        <li>
                          <Link className={footerTextClasses} to="#">{t('footer.v2.privacy')}</Link>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                </Container>
              </div>
            </section>
            :
            <>
              {children}
            </>
      }
    </footer >
  )
}